import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './components/ui/Button';
import { Card } from './components/ui/Card';
import { Input } from './components/ui/Input';
import type { z } from 'zod';
import {
  AdminOkResponseSchema,
  AdminTeamArtifactBlockSchema,
  AdminTeamDetailResponseSchema,
  AdminTeamMembersResponseSchema,
  AdminTeamResponseSchema,
  AdminTeamsResponseSchema,
  AdminUserDetailResponseSchema,
  AdminUsersResponseSchema,
  AdminUserUpdateResponseSchema,
} from '@shared/schemas/admin-users-teams';
import { AdminArtifactTypesResponseSchema } from '@shared/schemas/admin-ops';
import { useApi } from './lib/api';
import type { ApiProblemError, ApiFetch } from './lib/apiFetchJson';
import { fetchJsonOnHttp } from './lib/fetchJsonOnHttp';
import { Select } from './components/ui/Select';
import { typography } from './theme';
import { Plus, Trash2, UserMinus, Users, User, X } from 'lucide-react';
import { toast } from 'sonner';
import AdminUserTypes from './AdminUserTypes';
import AdminRoles from './AdminRoles';

// ─── Types ───────────────────────────────────────────────────────────

type Selection =
  | { kind: 'team'; id: number }
  | { kind: 'user'; id: number }
  | null;

const USER_TYPE_OPTIONS = [
  { value: 'MEMBER', label: 'Member', description: 'Default: Categories, Accounts, Pipeline, Agenda' },
  { value: 'PRE_SALES', label: 'Pre-Sales (GTM Engineer)', description: 'Categories, Accounts, Pipeline, Agenda' },
  { value: 'POST_SALES', label: 'Post-Sales', description: 'Categories, Accounts, Agenda (no Pipeline)' },
  { value: 'REVOPS', label: 'RevOps', description: 'Categories, Accounts, Pipeline, Agenda' },
] as const;

// ─── Helpers ─────────────────────────────────────────────────────────

/**
 * An HTTP failure re-thrown as the plain `Error` the query and mutation
 * consumers render. `failure` is the fixed copy shown today; `pick` may return
 * a more specific message read off the server's problem body.
 */
function adminJson<S extends z.ZodType>(
  apiFetch: ApiFetch,
  schema: S,
  url: string,
  failure: string,
  init?: Parameters<ApiFetch>[1],
  pick?: (err: ApiProblemError) => string | undefined
): Promise<z.output<S>> {
  return fetchJsonOnHttp(apiFetch, schema, url, init, (err): never => {
    throw new Error(pick?.(err) || failure);
  });
}

const detailOf = (err: ApiProblemError) => err.detail;

const DEACTIVATED_SUFFIX = ' (Deactivated in Salesforce)';

function displayName(user: { ownerEmail: string | null; slackUserId: string }): string {
  return user.ownerEmail || user.slackUserId;
}

function shortName(user: { ownerEmail: string | null; slackUserId: string }): string {
  return user.ownerEmail?.split('@')[0] || user.slackUserId;
}

// ─── Component ───────────────────────────────────────────────────────

export default function AdminUsers() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  const { tab: tabParam } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const SUB_TABS = [
    { key: 'teams', label: 'Teams' },
    { key: 'user-types', label: 'User Types' },
    { key: 'roles', label: 'Roles' },
  ] as const;
  const activeTab: 'teams' | 'user-types' | 'roles' =
    tabParam === 'user-types' ? 'user-types' : tabParam === 'roles' ? 'roles' : 'teams';
  const setTab = (tab: string) => navigate(`/admin/users/${tab}`, { replace: true });

  const [selection, setSelection] = useState<Selection>(null);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState<string | null>(null);
  const [userSearch, setUserSearch] = useState('');

  // ─── Queries ─────────────────────────────────────────────────────

  const teamsQuery = useQuery({
    queryKey: ['admin', 'teams'],
    queryFn: () => adminJson(apiFetch, AdminTeamsResponseSchema, '/api/admin/teams', 'Failed to load teams'),
    staleTime: 0,
  });

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => adminJson(apiFetch, AdminUsersResponseSchema, '/api/admin/users', 'Failed to load users'),
    staleTime: 0,
  });

  const selectedTeamId = selection?.kind === 'team' ? selection.id : null;
  const selectedUserId = selection?.kind === 'user' ? selection.id : null;

  const teamDetailQuery = useQuery({
    queryKey: ['admin', 'teams', selectedTeamId],
    queryFn: () =>
      adminJson(apiFetch, AdminTeamDetailResponseSchema, `/api/admin/teams/${selectedTeamId}`, 'Failed to load team'),
    enabled: selectedTeamId !== null,
    staleTime: 0,
  });

  const userDetailQuery = useQuery({
    queryKey: ['admin', 'users', selectedUserId],
    queryFn: () =>
      adminJson(apiFetch, AdminUserDetailResponseSchema, `/api/admin/users/${selectedUserId}`, 'Failed to load user'),
    enabled: selectedUserId !== null,
    staleTime: 0,
  });

  const artifactTypesQuery = useQuery({
    queryKey: ['admin', 'artifact-types'],
    queryFn: () =>
      adminJson(apiFetch, AdminArtifactTypesResponseSchema, '/api/admin/artifact-types', 'Failed to load artifact types'),
    staleTime: 0,
  });

  // ─── Mutations ───────────────────────────────────────────────────

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    if (selectedTeamId) void queryClient.invalidateQueries({ queryKey: ['admin', 'teams', selectedTeamId] });
  };

  const createTeamMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      return adminJson(apiFetch, AdminTeamResponseSchema, '/api/admin/teams', 'Failed to create team', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
    },
    onSuccess: (data) => {
      toast.success(`Team "${data.name}" created`);
      invalidateAll();
      setShowCreateTeam(false);
      setNewTeamName('');
      setSelection({ kind: 'team', id: data.id });
    },
  });

  const updateTeamMutation = useMutation({
    mutationFn: async (data: { teamId: number; name?: string; managerId?: number | null }) => {
      const { teamId, ...body } = data;
      return adminJson(
        apiFetch,
        AdminTeamResponseSchema,
        `/api/admin/teams/${teamId}`,
        'Failed to update team',
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
        detailOf
      );
    },
    onSuccess: () => {
      toast.success('Team updated');
      invalidateAll();
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: async (teamId: number) => {
      return adminJson(apiFetch, AdminOkResponseSchema, `/api/admin/teams/${teamId}`, 'Failed to delete team', {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      toast.success('Team deleted');
      setSelection(null);
      invalidateAll();
    },
  });

  const setMembersMutation = useMutation({
    mutationFn: async (data: { teamId: number; userIds: number[] }) => {
      return adminJson(
        apiFetch,
        AdminTeamMembersResponseSchema,
        `/api/admin/teams/${data.teamId}/members`,
        'Failed to update members',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userIds: data.userIds }),
        }
      );
    },
    onSuccess: () => {
      toast.success('Members updated');
      invalidateAll();
    },
  });

  const addBlockMutation = useMutation({
    mutationFn: async (data: { teamId: number; artifactSlug: string; reason?: string }) => {
      return adminJson(
        apiFetch,
        AdminTeamArtifactBlockSchema,
        `/api/admin/teams/${data.teamId}/artifact-blocks`,
        'Failed to add block',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ artifactSlug: data.artifactSlug, reason: data.reason }),
        },
        (err) => (err.title === 'block_already_exists' ? 'Block already exists' : undefined)
      );
    },
    onSuccess: () => {
      toast.success('Artifact block added');
      invalidateAll();
    },
  });

  const removeBlockMutation = useMutation({
    mutationFn: async (data: { teamId: number; blockId: number }) => {
      return adminJson(
        apiFetch,
        AdminOkResponseSchema,
        `/api/admin/teams/${data.teamId}/artifact-blocks/${data.blockId}`,
        'Failed to remove block',
        { method: 'DELETE' }
      );
    },
    onSuccess: () => {
      toast.success('Artifact block removed');
      invalidateAll();
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async (data: { userId: number; body: { userType?: string; copilotChannelId?: string } }) => {
      return adminJson(
        apiFetch,
        AdminUserUpdateResponseSchema,
        `/api/admin/users/${data.userId}`,
        'Failed to update user',
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data.body) },
        detailOf
      );
    },
    onSuccess: (data) => {
      toast.success('User updated');
      if (data?.botInChannel === false) {
        const channelLabel = data.copilotChannelName ? `#${data.copilotChannelName}` : 'the copilot channel';
        toast.warning(
          `Bot is not in ${channelLabel}. Copilot notifications will fail until it is invited.` +
            (data.botInviteError ? ` (${data.botInviteError})` : ''),
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      if (selectedUserId) void queryClient.invalidateQueries({ queryKey: ['admin', 'users', selectedUserId] });
    },
  });

  // ─── Derived data ────────────────────────────────────────────────

  const teams = useMemo(() => teamsQuery.data?.teams ?? [], [teamsQuery.data?.teams]);
  const users = useMemo(() => usersQuery.data?.users ?? [], [usersQuery.data?.users]);
  const selectedTeam = teamDetailQuery.data;
  const selectedUser = userDetailQuery.data;
  const artifactTypes = useMemo(
    () => artifactTypesQuery.data?.artifactTypes ?? [],
    [artifactTypesQuery.data?.artifactTypes],
  );

  // Users not on any team — available to add as members
  const unassignedUsers = useMemo(() => {
    return users.filter((u) => u.teamId === null);
  }, [users]);

  // User type filter
  const userTypes = useMemo(() => {
    const types = new Set(users.map((u) => u.userType));
    return Array.from(types).sort();
  }, [users]);
  const filteredUsers = useMemo(() => {
    let result = users;
    if (userTypeFilter) result = result.filter((u) => u.userType === userTypeFilter);
    if (userSearch.trim()) {
      const q = userSearch.toLowerCase();
      result = result.filter(
        (u) => (u.ownerEmail?.toLowerCase().includes(q) ?? false) || u.slackUserId.toLowerCase().includes(q),
      );
    }
    return result;
  }, [users, userTypeFilter, userSearch]);

  // ─── Team detail form state ──────────────────────────────────────

  const [editTeamName, setEditTeamName] = useState('');
  const [editManagerId, setEditManagerId] = useState<string>('');
  const [newBlockSlug, setNewBlockSlug] = useState('');
  const [newBlockReason, setNewBlockReason] = useState('');
  const [addMemberUserId, setAddMemberUserId] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberTypeFilter, setMemberTypeFilter] = useState<string | null>(null);

  // User detail form state
  const [editUserType, setEditUserType] = useState('');
  const [editCopilotChannel, setEditCopilotChannel] = useState('');

  // Reset team form when team changes
  useEffect(() => {
    if (selectedTeam) {
      setEditTeamName(selectedTeam.name);
      setEditManagerId(selectedTeam.managerId?.toString() || '');
    }
    setNewBlockSlug('');
    setNewBlockReason('');
    setAddMemberUserId('');
    setMemberSearch('');
    setMemberTypeFilter(null);
  }, [selectedTeam]);

  // Reset user form when user changes
  useEffect(() => {
    if (selectedUser) {
      setEditUserType(selectedUser.userType);
      setEditCopilotChannel(selectedUser.copilotChannelId ?? '');
    }
  }, [selectedUser]);

  // Available artifact types (exclude already blocked on this team)
  const blockedSlugs = useMemo(() => {
    return new Set((selectedTeam?.artifactBlocks || []).map((b) => b.artifactSlug));
  }, [selectedTeam]);

  const availableArtifactTypes = useMemo(() => {
    return artifactTypes.filter((t) => !blockedSlugs.has(t.slug));
  }, [artifactTypes, blockedSlugs]);

  // Users available to add as members to the current team (not on any team)
  const addableMembers = useMemo(() => {
    if (!selectedTeam) return [];
    const memberIds = new Set(selectedTeam.members.map((m) => m.id));
    return users.filter((u) => u.teamId === null && !memberIds.has(u.id) && !u.deactivatedInSfdc);
  }, [users, selectedTeam]);

  // Departed users already seated on the selected team stay visible, badged.
  // Invariant: `users` holds every emailed user, and null-email users are never departed.
  const deactivatedUserIds = useMemo(
    () => new Set(users.filter((u) => u.deactivatedInSfdc).map((u) => u.id)),
    [users],
  );

  // Addable members filtered by search + type
  const addableMemberTypes = useMemo(() => {
    const types = new Set(addableMembers.map((u) => u.userType));
    return Array.from(types).sort();
  }, [addableMembers]);

  const filteredAddableMembers = useMemo(() => {
    let result = addableMembers;
    if (memberTypeFilter) {
      result = result.filter((u) => u.userType === memberTypeFilter);
    }
    if (memberSearch.trim()) {
      const q = memberSearch.toLowerCase();
      result = result.filter((u) => displayName(u).toLowerCase().includes(q));
    }
    return result;
  }, [addableMembers, memberTypeFilter, memberSearch]);

  // Manager options: users who are not already managing a different team
  const managerOptions = useMemo(() => {
    const managerTeamMap = new Map<number, number>();
    for (const t of teams) {
      if (t.managerId !== null) managerTeamMap.set(t.managerId, t.id);
    }
    return users.filter((u) => {
      const managingTeamId = managerTeamMap.get(u.id);
      return managingTeamId === selectedTeamId || (managingTeamId === undefined && !u.deactivatedInSfdc);
    });
  }, [users, teams, selectedTeamId]);

  // ─── Handlers ────────────────────────────────────────────────────

  const handleCreateTeam = () => {
    if (!newTeamName.trim()) return;
    createTeamMutation.mutate({ name: newTeamName.trim() });
  };

  const handleSaveTeam = () => {
    if (!selectedTeamId) return;
    const managerId = editManagerId ? parseInt(editManagerId, 10) : null;
    updateTeamMutation.mutate({ teamId: selectedTeamId, name: editTeamName, managerId });
  };

  const handleDeleteTeam = () => {
    if (!selectedTeamId) return;
    if (!window.confirm('Delete this team? Members will become unassigned.')) return;
    deleteTeamMutation.mutate(selectedTeamId);
  };

  const handleRemoveMember = (memberId: number) => {
    if (!selectedTeam) return;
    const newIds = selectedTeam.members.filter((m) => m.id !== memberId).map((m) => m.id);
    setMembersMutation.mutate({ teamId: selectedTeam.id, userIds: newIds });
  };

  const _handleAddMember = () => {
    if (!selectedTeam || !addMemberUserId) return;
    const userId = parseInt(addMemberUserId, 10);
    const newIds = [...selectedTeam.members.map((m) => m.id), userId];
    setMembersMutation.mutate({ teamId: selectedTeam.id, userIds: newIds });
    setAddMemberUserId('');
  };

  const handleAddBlock = () => {
    if (!selectedTeamId || !newBlockSlug) return;
    addBlockMutation.mutate({
      teamId: selectedTeamId,
      artifactSlug: newBlockSlug,
      reason: newBlockReason || undefined,
    });
    setNewBlockSlug('');
    setNewBlockReason('');
  };

  const handleRemoveBlock = (blockId: number) => {
    if (!selectedTeamId) return;
    removeBlockMutation.mutate({ teamId: selectedTeamId, blockId });
  };

  const handleSaveUser = () => {
    if (!selectedUserId) return;
    updateUserMutation.mutate({ userId: selectedUserId, body: { userType: editUserType } });
  };

  const handleSaveCopilotChannel = () => {
    if (!selectedUserId) return;
    updateUserMutation.mutate({ userId: selectedUserId, body: { copilotChannelId: editCopilotChannel.trim() } });
  };

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <div className="max-w-5xl mx-auto p-4 font-sans">
      <div className="flex items-center gap-1 border-b border-nightshade-200 mb-6">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setTab(tab.key)}
            className={`px-3 py-3 text-sm border-b-2 ${
              activeTab === tab.key
                ? 'border-blueberry-500 text-nightshade-900 font-medium'
                : 'border-transparent text-nightshade-500 hover:text-nightshade-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'user-types' && <AdminUserTypes />}

      {activeTab === 'roles' && <AdminRoles />}

      {activeTab === 'teams' && (usersQuery.isLoading || teamsQuery.isLoading ? (
        <Card>
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-40 rounded bg-nightshade-200" />
            <div className="h-20 rounded bg-nightshade-100" />
          </div>
        </Card>
      ) : usersQuery.error || teamsQuery.error ? (
        <div className="text-content-danger">
          Error: {((usersQuery.error || teamsQuery.error) as Error).message}
        </div>
      ) : <>
      <h2 className={`${typography.headingMd} mb-1`}>Team Management</h2>
      <p className="mb-6 text-sm text-content-secondary">
        Organize users into teams, assign managers, and control artifact visibility per team.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* ─── Left sidebar ─────────────────────────────────────── */}
        <div className="md:col-span-1 space-y-4">
          {/* Teams section */}
          <Card>
            <div className="flex items-center justify-between mb-3">
              <h3 className={typography.headingSm}>Teams</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateTeam(!showCreateTeam)}
              >
                <Plus className="h-3.5 w-3.5" />
                New
              </Button>
            </div>

            {/* Create team inline form */}
            {showCreateTeam && (
              <div className="mb-3 p-3 rounded-lg bg-nightshade-50 space-y-2">
                <Input
                  placeholder="Team name"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateTeam()}
                  autoFocus
                />
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleCreateTeam}
                    disabled={!newTeamName.trim() || createTeamMutation.isPending}
                    loading={createTeamMutation.isPending}
                  >
                    Create
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowCreateTeam(false); setNewTeamName(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Team list */}
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {teams.length === 0 ? (
                <p className="text-xs text-content-tertiary py-4 text-center">No teams yet</p>
              ) : (
                teams.map((team) => (
                  <button
                    key={team.id}
                    onClick={() => setSelection({ kind: 'team', id: team.id })}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      selection?.kind === 'team' && selection.id === team.id
                        ? 'bg-blueberry-50 text-blueberry-900 ring-1 ring-blueberry-200'
                        : 'hover:bg-nightshade-50 text-content-secondary'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Users className="h-3.5 w-3.5 shrink-0 text-nightshade-400" />
                      <span className="font-medium truncate text-content-primary">{team.name}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 ml-5.5 text-xs text-content-tertiary">
                      {team.manager && (
                        <span className="truncate">
                          Mgr: {shortName(team.manager)}
                        </span>
                      )}
                      <span>{team.memberCount} member{team.memberCount !== 1 ? 's' : ''}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </Card>

          {/* Users section */}
          <Card>
            <h3 className={`${typography.headingSm} mb-2`}>Users</h3>
            <Input
              placeholder="Search by email or Slack ID..."
              value={userSearch}
              onChange={(e) => setUserSearch((e.target as HTMLInputElement).value)}
              className="mb-2"
            />
            <div className="flex items-center gap-1.5 mb-3 flex-wrap">
              <button
                onClick={() => setUserTypeFilter(null)}
                className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                  !userTypeFilter
                    ? 'bg-nightshade-900 text-white'
                    : 'bg-nightshade-100 text-nightshade-600 hover:bg-nightshade-200'
                }`}
              >
                All ({users.length})
              </button>
              {userTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setUserTypeFilter(userTypeFilter === type ? null : type)}
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                    userTypeFilter === type
                      ? 'bg-blueberry-500 text-white'
                      : 'bg-nightshade-100 text-nightshade-600 hover:bg-nightshade-200'
                  }`}
                >
                  {type.replace('_', '-')} ({users.filter((u) => u.userType === type).length})
                </button>
              ))}
            </div>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <p className="text-xs text-content-tertiary py-4 text-center">No matching users</p>
              ) : filteredUsers.map((user) => (
                <button
                  key={user.id}
                  onClick={() => setSelection({ kind: 'user', id: user.id })}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    selection?.kind === 'user' && selection.id === user.id
                      ? 'bg-blueberry-50 text-blueberry-900 ring-1 ring-blueberry-200'
                      : 'hover:bg-nightshade-50 text-content-secondary'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 shrink-0 text-nightshade-400" />
                    <span className="font-medium truncate text-content-primary">
                      {displayName(user)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 ml-5.5 text-xs text-content-tertiary">
                    <span className="px-1.5 py-0.5 rounded bg-nightshade-100 text-nightshade-600">
                      {user.userType.replace('_', '-')}
                    </span>
                    <span className="truncate">
                      {user.team ? user.team.name : 'No team'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* ─── Right panel ──────────────────────────────────────── */}
        <Card className="md:col-span-2">
          {selection === null ? (
            <div className="text-center text-content-tertiary py-16">
              Select a team or user to view details
            </div>
          ) : selection.kind === 'team' ? (
            /* ─── Team detail panel ─────────────────────────────── */
            teamDetailQuery.isLoading || !selectedTeam ? (
              <div className="animate-pulse space-y-4">
                <div className="h-4 w-40 rounded bg-nightshade-200" />
                <div className="h-20 rounded bg-nightshade-100" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Team name (editable) */}
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">
                    Team Name
                  </label>
                  <Input
                    value={editTeamName}
                    onChange={(e) => setEditTeamName(e.target.value)}
                  />
                </div>

                {/* Manager selector */}
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">
                    Manager
                  </label>
                  <Select
                    value={editManagerId}
                    onChange={(e) => setEditManagerId((e.target as HTMLSelectElement).value)}
                  >
                    <option value="">No manager</option>
                    {managerOptions.map((u) => (
                      <option key={u.id} value={u.id}>
                        {displayName(u)}{u.deactivatedInSfdc ? DEACTIVATED_SUFFIX : ''}
                      </option>
                    ))}
                  </Select>
                  <p className="mt-1 text-xs text-content-tertiary">
                    The manager controls this team's artifact visibility settings.
                  </p>
                </div>

                <Button
                  variant="primary"
                  onClick={handleSaveTeam}
                  disabled={updateTeamMutation.isPending || !editTeamName.trim()}
                  loading={updateTeamMutation.isPending}
                >
                  Save Team
                </Button>

                {/* Members list */}
                <div className="border-t border-secondary pt-4">
                  <h4 className="text-sm font-semibold text-content-primary mb-3">
                    Members ({selectedTeam.members.length})
                  </h4>

                  {selectedTeam.members.length > 0 ? (
                    <div className="space-y-1.5 mb-4">
                      {selectedTeam.members.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center justify-between px-3 py-2 bg-nightshade-50 rounded-lg"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <User className="h-3.5 w-3.5 shrink-0 text-nightshade-400" />
                            <span className="text-sm text-content-primary truncate">
                              {displayName(member)}
                            </span>
                            {deactivatedUserIds.has(member.id) && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-nightshade-100 text-content-tertiary shrink-0">
                                Deactivated in Salesforce
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="text-xs text-pomegranate-600 hover:text-pomegranate-700 flex items-center gap-1 shrink-0 ml-2"
                            disabled={setMembersMutation.isPending}
                            title="Remove from team"
                          >
                            <UserMinus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-content-tertiary mb-4">
                      No members assigned to this team.
                    </p>
                  )}

                  {/* Add member picker */}
                  {addableMembers.length > 0 && (
                    <div>
                      <label className="block text-xs text-content-tertiary mb-1">
                        Add Members
                      </label>
                      <Input
                        placeholder="Search users..."
                        value={memberSearch}
                        onChange={(e) => setMemberSearch((e.target as HTMLInputElement).value)}
                        className="mb-2"
                      />
                      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                        <button
                          onClick={() => setMemberTypeFilter(null)}
                          className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                            !memberTypeFilter
                              ? 'bg-nightshade-900 text-white'
                              : 'bg-nightshade-100 text-nightshade-600 hover:bg-nightshade-200'
                          }`}
                        >
                          All ({addableMembers.length})
                        </button>
                        {addableMemberTypes.map((type) => (
                          <button
                            key={type}
                            onClick={() => setMemberTypeFilter(memberTypeFilter === type ? null : type)}
                            className={`px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                              memberTypeFilter === type
                                ? 'bg-blueberry-500 text-white'
                                : 'bg-nightshade-100 text-nightshade-600 hover:bg-nightshade-200'
                            }`}
                          >
                            {type.replace('_', '-')} ({addableMembers.filter((u) => u.userType === type).length})
                          </button>
                        ))}
                      </div>
                      <div className="border border-secondary rounded-lg max-h-[200px] overflow-y-auto">
                        {filteredAddableMembers.length === 0 ? (
                          <p className="text-xs text-content-tertiary p-3">No matching users.</p>
                        ) : (
                          filteredAddableMembers.map((u) => (
                            <button
                              key={u.id}
                              onClick={() => {
                                setAddMemberUserId(String(u.id));
                                // Immediately add
                                const currentMembers = selectedTeam?.members.map((m) => m.id) || [];
                                setMembersMutation.mutate({
                                  teamId: selectedTeamId!,
                                  userIds: [...currentMembers, u.id],
                                });
                              }}
                              disabled={setMembersMutation.isPending}
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-nightshade-50 transition-colors flex items-center justify-between gap-2 border-b border-secondary last:border-b-0"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <User className="h-3.5 w-3.5 shrink-0 text-nightshade-400" />
                                <span className="truncate text-content-secondary">{displayName(u)}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-nightshade-100 text-nightshade-500 shrink-0">
                                  {u.userType.replace('_', '-')}
                                </span>
                              </div>
                              <Plus className="h-3.5 w-3.5 shrink-0 text-nightshade-400" />
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                  {addableMembers.length === 0 && unassignedUsers.length === 0 && (
                    <p className="text-xs text-content-tertiary">
                      All users are assigned to teams.
                    </p>
                  )}
                </div>

                {/* Artifact blocklist */}
                <div className="border-t border-secondary pt-4">
                  <h4 className="text-sm font-semibold text-content-primary mb-1">
                    Artifact Blocklist
                  </h4>
                  <p className="text-xs text-content-tertiary mb-3">
                    Artifact types blocked here will be hidden from all members of this team.
                  </p>

                  {selectedTeam.artifactBlocks.length > 0 ? (
                    <div className="space-y-1.5 mb-4">
                      {selectedTeam.artifactBlocks.map((block) => (
                        <div
                          key={block.id}
                          className="flex items-center justify-between px-3 py-2 bg-nightshade-50 rounded-lg"
                        >
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-content-primary">
                              {block.artifactSlug}
                            </span>
                            {block.reason && (
                              <span className="ml-2 text-xs text-content-tertiary">
                                ({block.reason})
                              </span>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveBlock(block.id)}
                            className="text-xs text-pomegranate-600 hover:text-pomegranate-700 shrink-0 ml-2"
                            disabled={removeBlockMutation.isPending}
                            title="Remove block"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-content-tertiary mb-4">
                      No artifact types blocked. Members see all artifacts.
                    </p>
                  )}

                  {availableArtifactTypes.length > 0 && (
                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <label className="block text-xs text-content-tertiary mb-1">
                          Artifact Type
                        </label>
                        <Select
                          value={newBlockSlug}
                          onChange={(e) => setNewBlockSlug((e.target as HTMLSelectElement).value)}
                        >
                          <option value="">Select artifact type...</option>
                          {availableArtifactTypes.map((t) => (
                            <option key={t.slug} value={t.slug}>
                              {t.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div className="flex-1">
                        <label className="block text-xs text-content-tertiary mb-1">
                          Reason (optional)
                        </label>
                        <Input
                          value={newBlockReason}
                          onChange={(e) => setNewBlockReason(e.target.value)}
                          placeholder="e.g., Not relevant for CSMs"
                        />
                      </div>
                      <Button
                        variant="neutral"
                        onClick={handleAddBlock}
                        disabled={!newBlockSlug || addBlockMutation.isPending}
                        loading={addBlockMutation.isPending}
                      >
                        Add
                      </Button>
                    </div>
                  )}
                </div>

                {/* Delete team */}
                <div className="border-t border-secondary pt-4">
                  <Button
                    variant="dangerOutline"
                    size="sm"
                    onClick={handleDeleteTeam}
                    disabled={deleteTeamMutation.isPending}
                    loading={deleteTeamMutation.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete Team
                  </Button>
                </div>
              </div>
            )
          ) : (
            /* ─── User detail panel ─────────────────────────────── */
            userDetailQuery.isLoading || !selectedUser ? (
              <div className="animate-pulse space-y-4">
                <div className="h-4 w-40 rounded bg-nightshade-200" />
                <div className="h-20 rounded bg-nightshade-100" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* User info */}
                <div>
                  <h3 className="text-sm font-semibold text-content-primary mb-2">User Info</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-content-tertiary">Email</div>
                    <div className="font-medium text-content-primary">
                      {selectedUser.ownerEmail || '\u2014'}
                    </div>
                    <div className="text-content-tertiary">Slack ID</div>
                    <div className="font-mono text-xs text-content-secondary">
                      {selectedUser.slackUserId}
                    </div>
                  </div>
                </div>

                {/* User type */}
                <div>
                  <label className="block text-sm font-medium text-content-secondary mb-1">
                    User Type
                  </label>
                  <Select
                    value={editUserType}
                    onChange={(e) => setEditUserType((e.target as HTMLSelectElement).value)}
                  >
                    {USER_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} — {opt.description}
                      </option>
                    ))}
                  </Select>
                  <p className="mt-1 text-xs text-content-tertiary">
                    Controls which app surfaces the user can see.
                  </p>
                </div>

                <Button
                  variant="primary"
                  onClick={handleSaveUser}
                  disabled={updateUserMutation.isPending || editUserType === selectedUser.userType}
                  loading={updateUserMutation.isPending}
                >
                  Save Changes
                </Button>

                {/* Copilot channel (editable) */}
                <div className="border-t border-secondary pt-4">
                  <h4 className="text-sm font-semibold text-content-primary mb-1">Copilot Channel</h4>
                  <p className="text-xs text-content-tertiary mb-2">
                    The Slack channel where this user's copilot notifications are delivered.{' '}
                    {selectedUser.copilotChannelName ? (
                      <>Current: <span className="font-mono text-content-secondary">#{selectedUser.copilotChannelName}</span></>
                    ) : selectedUser.copilotChannelId ? (
                      <>Current: <span className="font-mono text-content-secondary">{selectedUser.copilotChannelId}</span></>
                    ) : (
                      'Not set.'
                    )}
                  </p>
                  <Input
                    placeholder="Slack channel ID (e.g. C0AD09EPTU3)"
                    value={editCopilotChannel}
                    onChange={(e) => setEditCopilotChannel((e.target as HTMLInputElement).value)}
                    className="mb-2"
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveCopilotChannel}
                    disabled={
                      updateUserMutation.isPending ||
                      editCopilotChannel.trim() === (selectedUser.copilotChannelId ?? '')
                    }
                    loading={updateUserMutation.isPending}
                  >
                    Save Channel
                  </Button>
                  <p className="mt-1 text-xs text-content-tertiary">
                    Paste the Slack <span className="font-medium">channel ID</span> (starts with <span className="font-mono">C…</span>) — in Slack, open the channel, click its name, and copy the Channel ID at the bottom of the About tab. The channel is verified and the bot invited automatically; clearing the field disables copilot delivery.
                  </p>
                </div>

                {/* Team assignment (read-only) */}
                <div className="border-t border-secondary pt-4">
                  <h4 className="text-sm font-semibold text-content-primary mb-2">Team</h4>
                  {selectedUser.team ? (
                    <button
                      onClick={() => setSelection({ kind: 'team', id: selectedUser.team!.id })}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blueberry-50 text-blueberry-700 text-sm hover:bg-blueberry-100 transition-colors"
                    >
                      <Users className="h-3.5 w-3.5" />
                      {selectedUser.team.name}
                    </button>
                  ) : (
                    <p className="text-sm text-content-tertiary">Not assigned to any team</p>
                  )}
                </div>
              </div>
            )
          )}
        </Card>
      </div>
      </>)}
    </div>
  );
}
